import { cn } from "@/lib/cn";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * DetailPageSkeleton
 *
 * Canonical reference: ianfiles/ui-design-system.md §2.12 + §3.6
 *
 * Render this until the primary detail-page query resolves. Three layout
 * choices:
 *   - `single`       — single-column stack (rare: Sites / Locations)
 *   - `two-column`   — narrow main + side
 *   - `three-column` — the dominant detail-page layout (default)
 *
 * Skeleton rows match the actual structure (breadcrumb stub, title stub,
 * three or four section stubs) so the swap-in is visually quiet.
 */
export interface DetailPageSkeletonProps {
  layout?: "single" | "two-column" | "three-column";
  /** Number of section blocks to render in the main column (default 3). */
  sectionCount?: number;
  className?: string;
}

export function DetailPageSkeleton({
  layout = "three-column",
  sectionCount = 3,
  className,
}: DetailPageSkeletonProps) {
  const sideStack = (
    <div className="space-y-4">
      <SectionSkeleton lines={3} />
      <SectionSkeleton lines={2} />
    </div>
  );

  return (
    <div className={cn("space-y-4", className)}>
      {/* Breadcrumb + title stub */}
      <div className="space-y-2">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-8 w-72" />
      </div>

      {layout === "single" ? (
        <div className="space-y-4">
          {Array.from({ length: sectionCount }).map((_, i) => (
            <SectionSkeleton key={i} lines={3 + (i % 2)} />
          ))}
        </div>
      ) : (
        <div
          className={cn(
            "grid grid-cols-1 gap-4",
            layout === "three-column" ? "lg:grid-cols-3" : "lg:grid-cols-2"
          )}
        >
          <div
            className={cn(
              "space-y-4 min-w-0",
              layout === "three-column" && "lg:col-span-2"
            )}
          >
            {Array.from({ length: sectionCount }).map((_, i) => (
              <SectionSkeleton key={i} lines={3 + (i % 2)} />
            ))}
          </div>
          <div className="min-w-0">{sideStack}</div>
        </div>
      )}
    </div>
  );
}

function SectionSkeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="bg-card border border-card-border rounded-lg shadow-sm">
      <div className="flex items-center justify-between gap-2 px-4 py-3">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-8 w-8" />
      </div>
      <div className="px-4 pb-4 space-y-2">
        {Array.from({ length: lines }).map((_, i) => (
          <Skeleton
            key={i}
            className={cn("h-4", i % 2 === 0 ? "w-3/4" : "w-1/2")}
          />
        ))}
      </div>
    </div>
  );
}
